import React from 'react';

import { Box, Chip, Stack, Tooltip, Typography, alpha } from '@mui/material';

import { departureActionDetail, departureActionGroups } from './departureActions';
import { flagLabels } from './logic';
import { actionColour, actionText, reviewStatusSx, wrappingPillSx } from './palette';
import { daysAtStatus, reviewGroupTooltip, reviewStatuses, statusDaysLabel, statusOf } from './reviewLogic';
import { CommandDeparture, CommandFlag, CostBasis } from './types';

export default function DepartureActionPills({
  rows,
  basis,
  selected,
  today,
}: {
  rows: Array<CommandDeparture>;
  basis: CostBasis;
  selected?: CommandFlag | 'all' | null;
  today?: string;
}) {
  if (rows.some((d) => statusOf(d, basis))) {
    return (
      <Stack direction="row" flexWrap="wrap" gap={0.5}>
        {reviewStatuses
          .filter((status) => rows.some((d) => statusOf(d, basis) === status))
          .map((status) => (
            <Tooltip
              key={status}
              title={
                <Box sx={{ whiteSpace: 'pre-line' }}>
                  {reviewGroupTooltip(
                    rows.filter((d) => statusOf(d, basis) === status),
                    basis,
                  )}
                </Box>
              }
            >
              <Stack
                direction="row"
                alignItems="center"
                gap={0.5}
                sx={{
                  minWidth: 0,
                  flexWrap: 'wrap',
                  '@media (min-width: 1280px)': { whiteSpace: 'nowrap' },
                }}
              >
                <Chip
                  size="small"
                  label={`${status} ${rows.filter((d) => statusOf(d, basis) === status).length}`}
                  sx={{ ...reviewStatusSx(status), ...wrappingPillSx }}
                  color={status === 'Urgent' ? 'error' : status === 'Needs Action' ? 'warning' : 'default'}
                />
                {today &&
                  (() => {
                    const departures = rows.filter(
                      (d) => statusOf(d, basis) === status && daysAtStatus(d, basis, today) != null,
                    );
                    if (!departures.length) return null;
                    const longest = Math.max(...departures.map((d) => daysAtStatus(d, basis, today)));
                    const longestDeparture = departures.find((d) => daysAtStatus(d, basis, today) === longest);
                    return (
                      <Typography
                        variant="caption"
                        title="Longest observed run in this status group"
                        color={
                          longest >= 14 && ['Urgent', 'Needs Action'].includes(status)
                            ? 'warning.main'
                            : 'text.secondary'
                        }
                      >
                        · {statusDaysLabel(longestDeparture, basis, today)}
                      </Typography>
                    );
                  })()}
              </Stack>
            </Tooltip>
          ))}
      </Stack>
    );
  }
  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {departureActionGroups(rows, basis).map(({ flag, rows: departures }) => (
        <Tooltip
          key={flag}
          title={
            <Box sx={{ whiteSpace: 'pre-line' }}>
              {departures.map((d) => departureActionDetail(d, basis)).join('\n')}
            </Box>
          }
        >
          <Chip
            size="small"
            data-departure-action={flag}
            data-count={departures.length}
            aria-label={`Departures: ${flagLabels[flag]} ${departures.length}`}
            label={`${flagLabels[flag]} ${departures.length}`}
            sx={{
              fontSize: 11,
              fontWeight: 700,
              color: actionText(flag),
              bgcolor: alpha(actionColour(flag), 0.14),
              border: '2px solid',
              borderColor: selected === flag ? actionColour(flag) : 'transparent',
              maxWidth: '100%',
              height: 'auto',
              minHeight: 26,
              '& .MuiChip-label': { whiteSpace: 'normal', px: 0.75, py: 0.25 },
            }}
          />
        </Tooltip>
      ))}
    </Stack>
  );
}
